import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { pushToast } from '../../lib/events';
import { createInvite, invitesQuery, revokeInvite, type InviteRole } from '../../lib/rooms';

interface HostRailInvitesProps {
  roomId: number;
}

const ROLE_OPTIONS: InviteRole[] = ['COHOST', 'GUEST', 'VIEWER'];
const EXPIRY_PRESETS = [
  { label: '1h', minutes: 60 },
  { label: '24h', minutes: 1440 },
  { label: '7d', minutes: 10080 },
] as const;

/** Create-link form (role + expiry preset) plus the active-invite list (copy + revoke). */
export function HostRailInvites({ roomId }: HostRailInvitesProps) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<InviteRole>('GUEST');
  const [minutes, setMinutes] = useState<number>(1440);

  const { data: invites } = useQuery(invitesQuery(roomId));

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: invitesQuery(roomId).queryKey });
  }

  const createMutation = useMutation({
    mutationFn: createInvite,
    onSuccess: invalidate,
    onError: () => pushToast('Could not create the invite.'),
  });
  const revokeMutation = useMutation({
    mutationFn: revokeInvite,
    onSuccess: invalidate,
    onError: () => pushToast('Could not revoke the invite.'),
  });

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(new URL(url, window.location.origin).toString());
      pushToast('Invite link copied.', 'success');
    } catch {
      pushToast('Could not copy the link — copy it from the address shown.');
    }
  }

  const active = (invites ?? []).filter((invite) => invite.revokedAt === null);

  return (
    <section>
      <div className="flex items-baseline gap-4">
        <span className="eyebrow">Invites</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select
          value={role}
          onChange={(event) => setRole(event.target.value as InviteRole)}
          aria-label="Invite role"
          className="min-h-tap rounded-md border border-border-strong bg-bg px-2 text-sm text-text"
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <div className="flex gap-1" role="group" aria-label="Expiry">
          {EXPIRY_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              aria-pressed={minutes === preset.minutes}
              onClick={() => setMinutes(preset.minutes)}
              className={[
                'min-h-tap rounded-md border px-2 text-xs font-semibold',
                minutes === preset.minutes ? 'border-accent text-accent' : 'border-border-strong text-text-muted',
              ].join(' ')}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={createMutation.isPending}
          onClick={() => createMutation.mutate({ roomId, role, expiresInMinutes: minutes })}
          className="min-h-tap flex-1 rounded-md border border-accent-border bg-accent-dim px-3 text-sm font-semibold text-accent disabled:opacity-50"
        >
          Create link
        </button>
      </div>

      <ul className="mt-3 flex flex-col gap-1.5">
        {active.length === 0 && <li className="text-sm text-text-faint">No active invites.</li>}
        {active.map((invite) => (
          <li key={invite.id} className="flex items-center justify-between gap-2 rounded-md border border-border-strong px-3 py-2">
            <div className="min-w-0">
              <span className="eyebrow block">{invite.role}</span>
              <span className="telemetry block truncate text-text-muted">{invite.url}</span>
            </div>
            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => void copyLink(invite.url)}
                className="min-h-tap rounded-md border border-border-strong px-2 text-xs font-semibold text-text-muted hover:border-accent-border hover:text-text"
              >
                Copy
              </button>
              <button
                type="button"
                disabled={revokeMutation.isPending}
                onClick={() => revokeMutation.mutate({ roomId, inviteId: invite.id })}
                className="min-h-tap rounded-md border border-border-strong px-2 text-xs font-semibold text-text-muted hover:border-signal hover:text-signal disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
